import React, { Component } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Modal, 
  Form, 
  Select, 
  message, 
  Spin, 
  Card, 
  Row, 
  Col,
  Tag,
  Drawer,
  Collapse,
  DatePicker,
  Tooltip
} from 'antd';
import ReactToPrint from 'react-to-print';
import * as XLSX from 'xlsx';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import * as serverconfig from '../../serverconn';
import moment from 'moment';
import PatientForm from '../components/PatientForm';
import PatientList from '../components/PatientList';

const { Panel } = Collapse;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

class PendingUmojaPickupsList extends Component {
  constructor(props) {
    super(props);
    this.printRef = React.createRef();
  }

  state = {
    searchText: '',
    searchedColumn: '',
    pendingPickups: [],
    datarequested: true,
    modalVisible: false,
    editModalVisible: false,
    selectedPickup: null,
    samples: [],
    healthCenters: [],
    collectionHubs: [],
    deliveryAgents: [],
    form: null,
    editForm: null,
    loading: false,
    saving: false,
    pickupFromType: 'health_center',
    destinationType: 'collection_hub',
    selectedSamplePriority: null,
    dateRange: null, // Will be set by backend default (2 weeks)
    // Patient-related state
    patientFormVisible: false,
    patientListVisible: false,
    selectedPatient: null,
    isEditingPatient: false,
    currentPickupId: null
  };

  componentDidMount() {
    this.loadData();
  }

  // Get user rights from props
  get userrights() {
    return this.props.userrights || {};
  }

  // Get user profile from props  
  get userprofile() {
    return this.props.userprofile || {};
  }

  loadData = async (dateRange = null) => {
    try {
      this.setState({ datarequested: true });
      
      // Prepare search URL - use new search endpoint
      let pickupsUrl = `${serverconfig.backendserverurl}/api/pending_umoja_pickups/search_by_date_range/`;
      
      // Add date range parameters if provided
      if (dateRange && dateRange.length === 2) {
        const startDate = dateRange[0].format('YYYY-MM-DD');
        const endDate = dateRange[1].format('YYYY-MM-DD');
        pickupsUrl += `?start_date=${startDate}&end_date=${endDate}`;
      }
      // If no date range provided, endpoint will use 2-week default
      
      const [pickupsRes, samplesRes, healthCentersRes, collectionHubsRes, agentsRes] = await Promise.all([
        axios.get(pickupsUrl),
        axios.get(`${serverconfig.backendserverurl}/api/samples/`),
        axios.get(`${serverconfig.backendserverurl}/api/healthcentres/`),
        axios.get(`${serverconfig.backendserverurl}/api/collectionhubs/`),
        axios.get(`${serverconfig.backendserverurl}/api/delivery_agents/`)
      ]);

      // Handle the new response format
      const pickupsData = pickupsRes.data.results || pickupsRes.data;
      
      this.setState({
        pendingPickups: pickupsData,
        samples: samplesRes.data,
        healthCenters: healthCentersRes.data,
        collectionHubs: collectionHubsRes.data,
        deliveryAgents: agentsRes.data,
        datarequested: false
      });
      
      // Update date range if using default from backend
      if (pickupsRes.data.date_range && pickupsRes.data.date_range.default_range) {
        const backendStartDate = moment(pickupsRes.data.date_range.start_date);
        const backendEndDate = moment(pickupsRes.data.date_range.end_date);
        this.setState({
          dateRange: [backendStartDate, backendEndDate]
        });
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Failed to load data');
      this.setState({ datarequested: false });
    }
  };

  handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      this.setState({ dateRange: dates });
    }
  };

  handleSearchByDateRange = () => {
    if (this.state.dateRange && this.state.dateRange.length === 2) {
      this.loadData(this.state.dateRange);
    } else {
      message.warning('Please select a valid date range');
    }
  };

  setDateRangePreset = (days) => {
    const endDate = moment();
    const startDate = moment().subtract(days, 'days');
    const newRange = [startDate, endDate];
    
    this.setState({ dateRange: newRange }, () => {
      // Auto-search when preset is selected
      this.loadData(newRange);
    });
  };

  handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    this.setState({
      searchText: selectedKeys[0],
      searchedColumn: dataIndex,
    });
  };

  handleReset = clearFilters => {
    clearFilters();
    this.setState({ searchText: '' });
  };

  getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => {
            this.searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => this.handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
  });

  showCreateModal = () => {
    this.setState({ 
      modalVisible: true, 
      pickupFromType: 'health_center',
      destinationType: 'collection_hub'
    });
  };

  showEditModal = (pickup) => {
    // Determine pickup and destination types from new enhanced fields or legacy data
    let pickupType = pickup.pickup_location_type || 'health_center';
    let destType = pickup.destination_location_type || 'collection_hub';
    
    // Fallback to legacy field analysis if new fields are not available
    if (!pickup.pickup_location_type && pickup.pickup_from) {
      if (pickup.pickup_from.includes('Health Center:')) {
        pickupType = 'health_center';
      } else if (pickup.pickup_from.includes('Collection Hub:')) {
        pickupType = 'collection_hub';
      } else {
        pickupType = 'other';
      }
    }

    this.setState({ 
      editModalVisible: true, 
      selectedPickup: pickup,
      pickupFromType: pickupType,
      destinationType: destType
    });
  };

  handleCreate = async (values) => {
    try {
      this.setState({ saving: true });
      
      // Prepare the enhanced data for submission
      const submissionData = {
        date: values.date ? values.date.format('YYYY-MM-DD') : null,
        sample: values.sample || null,
        quantity: values.quantity || null,
        pickup_status: values.pickup_status || 'pending',
        // Priority is inherited from sample, no need to store separately
        notes: values.notes || null,
        source: 'dashboard', // Track that this was created from dashboard
        created_by: this.userprofile.username || 'Admin', // Use admin username
        
        // New enhanced pickup location fields
        pickup_location_type: this.state.pickupFromType || null,
        pickup_health_center: this.state.pickupFromType === 'health_center' ? (values.pickup_health_center || null) : null,
        pickup_collection_hub: this.state.pickupFromType === 'collection_hub' ? (values.pickup_collection_hub || null) : null,
        pickup_notes: values.pickup_notes || null,
        
        // New enhanced destination fields
        destination_location_type: this.state.destinationType || null,
        destination_health_center: this.state.destinationType === 'health_center' ? (values.destination_health_center || null) : null,
        destination_collection_hub: this.state.destinationType === 'collection_hub' ? (values.destination_collection_hub || null) : null,
        destination_notes: values.destination_notes || null,
        
        // Agent assignment (rider is the same as delivery agent)
        assigned_delivery_agent: values.assigned_delivery_agent || null,
        assigned_rider: values.assigned_delivery_agent || null,
        
        // Timing field
        scheduled_pickup_date: values.scheduled_pickup_date ? values.scheduled_pickup_date.format('YYYY-MM-DD HH:mm:ss') : null,
        
        // Legacy fields for backward compatibility
        health_center: values.pickup_health_center || values.destination_health_center || null,
        collection_hub: values.pickup_collection_hub || values.destination_collection_hub || null,
        pickup_from: values.pickup_from || `${this.state.pickupFromType === 'health_center' ? 'Health Center' : 'Collection Hub'} Pickup`
      };
      
      const response = await axios.post(
        `${serverconfig.backendserverurl}/api/pending_umoja_pickups/`,
        submissionData
      );
      
      message.success('Pending pickup created successfully');
      this.setState({ 
        modalVisible: false, 
        pickupFromType: 'health_center',
        destinationType: 'collection_hub'
      });
      this.loadData();
      
      // Open patient form for the newly created pickup
      if (response.data && response.data.id) {
        setTimeout(() => {
          this.showPatientForm(response.data.id);
        }, 1000); // Small delay to ensure the pickup is loaded
      }
    } catch (error) {
      console.error('Error creating pickup:', error);
      message.error(`Failed to create pickup: ${error.response?.data?.message || error.message}`);
    } finally {
      this.setState({ saving: false });
    }
  };

  handleEdit = async (values) => {
    try {
      this.setState({ saving: true });
      
      // Prepare the enhanced data for submission
      const submissionData = {
        date: values.date ? values.date.format('YYYY-MM-DD') : null,
        sample: values.sample,
        quantity: values.quantity,
        pickup_status: values.pickup_status,
        // Priority is inherited from sample, no need to store separately
        notes: values.notes,
        // Preserve the original source - don't change it during edit
        source: this.state.selectedPickup.source,
        
        // New enhanced pickup location fields
        pickup_location_type: this.state.pickupFromType || null,
        pickup_health_center: this.state.pickupFromType === 'health_center' ? (values.pickup_health_center || null) : null,
        pickup_collection_hub: this.state.pickupFromType === 'collection_hub' ? (values.pickup_collection_hub || null) : null,
        pickup_notes: values.pickup_notes || null,
        
        // New enhanced destination fields
        destination_location_type: this.state.destinationType || null,
        destination_health_center: this.state.destinationType === 'health_center' ? (values.destination_health_center || null) : null,
        destination_collection_hub: this.state.destinationType === 'collection_hub' ? (values.destination_collection_hub || null) : null,
        destination_notes: values.destination_notes || null,
        
        // Agent assignment (rider is the same as delivery agent)
        assigned_delivery_agent: values.assigned_delivery_agent || null,
        assigned_rider: values.assigned_delivery_agent || null,
        
        // Timing field
        scheduled_pickup_date: values.scheduled_pickup_date ? values.scheduled_pickup_date.format('YYYY-MM-DD HH:mm:ss') : null,
        
        // Failure reason if status is failed
        failure_reason: values.failure_reason || null,
        
        // Legacy fields for backward compatibility
        health_center: values.pickup_health_center || values.destination_health_center || null,
        collection_hub: values.pickup_collection_hub || values.destination_collection_hub || null,
        pickup_from: values.pickup_from || `${this.state.pickupFromType === 'health_center' ? 'Health Center' : 'Collection Hub'} Pickup`
      };
      
      const response = await axios.put(
        `${serverconfig.backendserverurl}/api/pending_umoja_pickups/${this.state.selectedPickup.id}/`,
        submissionData
      );
      
      message.success('Pending pickup updated successfully');
      this.setState({ 
        editModalVisible: false, 
        selectedPickup: null,
        pickupFromType: 'health_center',
        destinationType: 'collection_hub'
      });
      this.loadData();
    } catch (error) {
      console.error('Error updating pickup:', error);
      message.error(`Failed to update pickup: ${error.response?.data?.message || error.message}`);
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    try {
      await axios.delete(`${serverconfig.backendserverurl}/api/pending_umoja_pickups/${id}/`);
      message.success('Pending pickup deleted successfully');
      this.loadData();
    } catch (error) {
      console.error('Error deleting pickup:', error);
      message.error('Failed to delete pickup');
    }
  };

  // Export functions
  exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = this.state.pendingPickups.map(pickup => ({
        'Pickup Number': pickup.pickup_number || '',
        'Date': pickup.date || '',
        'Sample': pickup.sample_name || '',
        'Quantity': pickup.quantity || '',
        'Priority': pickup.sample?.priority?.toUpperCase() || 'NORMAL',
        'Status': pickup.pickup_status || '',
        'Pickup Location': pickup.pickup_location_display || '',
        'Destination': pickup.destination_location_display || '',
        'Assigned Agent': pickup.assigned_delivery_agent_name || '',
        'Rider': pickup.assigned_rider_name || '',
        'Scheduled Pickup': pickup.scheduled_pickup_date ? moment(pickup.scheduled_pickup_date).format('DD/MM/YYYY HH:mm') : '',
        'Notes': pickup.notes || '',
        'Pickup Notes': pickup.pickup_notes || '',
        'Destination Notes': pickup.destination_notes || '',
        'Created': pickup.time_stamp ? moment(pickup.time_stamp).format('DD/MM/YYYY HH:mm') : '',
        'Assigned At': pickup.assigned_at ? moment(pickup.assigned_at).format('DD/MM/YYYY HH:mm') : '',
        'Picked Up At': pickup.picked_up_at ? moment(pickup.picked_up_at).format('DD/MM/YYYY HH:mm') : '',
        'Delivered At': pickup.delivered_at ? moment(pickup.delivered_at).format('DD/MM/YYYY HH:mm') : ''
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pending Pickups');

      // Set column widths
      const colWidths = [
        { wch: 15 }, // Pickup Number
        { wch: 12 }, // Date
        { wch: 20 }, // Sample
        { wch: 10 }, // Quantity
        { wch: 12 }, // Priority
        { wch: 20 }, // Status
        { wch: 25 }, // Pickup Location
        { wch: 25 }, // Destination
        { wch: 20 }, // Assigned Agent
        { wch: 20 }, // Rider
        { wch: 18 }, // Scheduled Pickup
        { wch: 30 }, // Notes
        { wch: 30 }, // Pickup Notes
        { wch: 30 }, // Destination Notes
        { wch: 18 }, // Created
        { wch: 18 }, // Assigned At
        { wch: 18 }, // Picked Up At
        { wch: 18 }  // Delivered At
      ];
      ws['!cols'] = colWidths;

      // Generate filename with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      const filename = `Pending_Sample_Pickups_${timestamp}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      message.success(`Excel file exported successfully: ${filename}`);
    } catch (error) {
      console.error('Export to Excel failed:', error);
      message.error('Failed to export to Excel');
    }
  };

  // Printable component
  renderPrintableContent = () => {
    return (
      <div style={{ padding: '20px', fontSize: '12px' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: '0' }}>Umoja Sample Pickups Report</h2>
          <p style={{ margin: '5px 0', color: '#666' }}>
            Generated: {moment().format('DD/MM/YYYY HH:mm')} | 
            Total Records: {this.state.pendingPickups.length}
          </p>
        </div>
        
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Pickup #</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Date</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Sample</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Qty</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Priority</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Status</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>From</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>To</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Agent</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Scheduled</th>
            </tr>
          </thead>
          <tbody>
            {this.state.pendingPickups.map((pickup, index) => (
              <tr key={pickup.id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>{pickup.pickup_number}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>{pickup.date}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>{pickup.sample_name}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>{pickup.quantity}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                  {pickup.sample?.priority?.toUpperCase() || 'NORMAL'}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                  {pickup.pickup_status?.replace(/_/g, ' ').toUpperCase()}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: '10px' }}>
                  {pickup.pickup_location_display || '-'}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: '10px' }}>
                  {pickup.destination_location_display || '-'}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                  {pickup.assigned_delivery_agent_name || 'Unassigned'}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                  {pickup.scheduled_pickup_date ? 
                    moment(pickup.scheduled_pickup_date).format('DD/MM HH:mm') : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div style={{ marginTop: '20px', fontSize: '10px', color: '#666' }}>
          <p><strong>Legend:</strong></p>
          <p>Priority: LOW | NORMAL | HIGH | URGENT | EMERGENCY</p>
          <p>Status: PENDING | ASSIGNED | IN_TRANSIT | PICKED_UP | DELIVERED | CANCELLED | FAILED</p>
        </div>
      </div>
    );
  };

  handleSampleChange = (sampleId) => {
    const selectedSample = this.state.samples.find(sample => sample.id === sampleId);
    if (selectedSample) {
      this.setState({ selectedSamplePriority: selectedSample.priority });
    }
  };

  getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'assigned':
        return 'blue';
      case 'in_transit_to_pickup':
        return 'cyan';
      case 'at_pickup_location':
        return 'geekblue';
      case 'picked_up':
        return 'purple';
      case 'in_transit_to_destination':
        return 'magenta';
      case 'delivered':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'failed':
        return 'volcano';
      default:
        return 'default';
    }
  };

  getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockCircleOutlined />;
      case 'assigned':
        return <ClockCircleOutlined />;
      case 'in_transit_to_pickup':
      case 'in_transit_to_destination':
        return <EnvironmentOutlined />;
      case 'at_pickup_location':
        return <EnvironmentOutlined />;
      case 'picked_up':
      case 'delivered':
        return <CheckCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  renderCreateForm() {
    return (
      <Form
        layout="vertical"
        onFinish={this.handleCreate}
        initialValues={{
          pickup_status: 'pending'
        }}
      >
        {/* Basic Information */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="date"
              label="Pickup Date"
              rules={[{ required: true, message: 'Please select pickup date' }]}
            >
              <DatePicker 
                format="DD/MM/YYYY"
                placeholder="Select pickup date"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="sample"
              label="Sample Type"
              rules={[{ required: true, message: 'Please select sample type' }]}
            >
              <Select 
                placeholder="Select sample type"
                onChange={this.handleSampleChange}
              >
                {this.state.samples.map(sample => (
                  <Option key={sample.id} value={sample.id}>{sample.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[{ required: true, message: 'Please enter quantity' }]}
            >
              <Input type="number" placeholder="Enter quantity" min={1} />
            </Form.Item>
          </Col>
        </Row>


        {/* Location Configuration */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Pickup Location Type"
              rules={[{ required: true, message: 'Please select pickup location type' }]}
            >
              <Select 
                placeholder="Choose where to pickup samples from"
                onChange={(value) => this.setState({ pickupFromType: value })}
                value={this.state.pickupFromType}
              >
                <Option value="health_center">
                  <EnvironmentOutlined style={{ color: '#52c41a' }} /> Health Center
                  <div style={{ fontSize: '11px', color: '#666' }}>Medical facilities, clinics</div>
                </Option>
                <Option value="collection_hub">
                  <EnvironmentOutlined style={{ color: '#1890ff' }} /> Collection Hub
                  <div style={{ fontSize: '11px', color: '#666' }}>Central collection points</div>
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Destination Location Type"
              rules={[{ required: true, message: 'Please select destination location type' }]}
            >
              <Select 
                placeholder="Choose where to deliver samples to"
                onChange={(value) => this.setState({ destinationType: value })}
                value={this.state.destinationType}
              >
                <Option value="health_center">
                  <EnvironmentOutlined style={{ color: '#52c41a' }} /> Health Center
                  <div style={{ fontSize: '11px', color: '#666' }}>Medical facilities, clinics</div>
                </Option>
                <Option value="collection_hub">
                  <EnvironmentOutlined style={{ color: '#1890ff' }} /> Collection Hub
                  <div style={{ fontSize: '11px', color: '#666' }}>Central collection points</div>
                </Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Pickup Location */}
        <Row gutter={16}>
          <Col span={12}>
            {this.state.pickupFromType === 'health_center' && (
              <Form.Item
                name="pickup_health_center"
                label="Pickup Health Center"
                rules={[{ 
                  required: true, 
                  message: 'Please select pickup health center' 
                }]}
              >
                <Select 
                  placeholder="Choose which health center to pickup from"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {this.state.healthCenters.map(hc => (
                    <Option key={hc.id} value={hc.id}>{hc.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            
            {this.state.pickupFromType === 'collection_hub' && (
              <Form.Item
                name="pickup_collection_hub"
                label="Pickup Collection Hub"
                rules={[{ 
                  required: true, 
                  message: 'Please select pickup collection hub' 
                }]}
              >
                <Select 
                  placeholder="Choose which collection hub to pickup from"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {this.state.collectionHubs.map(ch => (
                    <Option key={ch.id} value={ch.id}>{ch.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            
            {!this.state.pickupFromType && (
              <Form.Item label="Pickup Location">
                <div style={{ 
                  padding: '10px', 
                  border: '1px dashed #ddd', 
                  borderRadius: '6px', 
                  color: '#999',
                  textAlign: 'center'
                }}>
                  Please select pickup location type first
                </div>
              </Form.Item>
            )}
          </Col>
          <Col span={12}>
            {this.state.destinationType === 'health_center' && (
              <Form.Item
                name="destination_health_center"
                label="Destination Health Center"
                rules={[{ 
                  required: true, 
                  message: 'Please select destination health center' 
                }]}
              >
                <Select 
                  placeholder="Choose which health center to deliver to"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {this.state.healthCenters.map(hc => (
                    <Option key={hc.id} value={hc.id}>{hc.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            
            {this.state.destinationType === 'collection_hub' && (
              <Form.Item
                name="destination_collection_hub"
                label="Destination Collection Hub"
                rules={[{ 
                  required: true, 
                  message: 'Please select destination collection hub' 
                }]}
              >
                <Select 
                  placeholder="Choose which collection hub to deliver to"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {this.state.collectionHubs.map(ch => (
                    <Option key={ch.id} value={ch.id}>{ch.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            
            {!this.state.destinationType && (
              <Form.Item label="Destination Location">
                <div style={{ 
                  padding: '10px', 
                  border: '1px dashed #ddd', 
                  borderRadius: '6px', 
                  color: '#999',
                  textAlign: 'center'
                }}>
                  Please select destination location type first
                </div>
              </Form.Item>
            )}
          </Col>
        </Row>

        {/* Location Notes */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="pickup_notes"
              label={
                this.state.pickupFromType === 'health_center' 
                  ? "Health Center Pickup Notes" 
                  : this.state.pickupFromType === 'collection_hub'
                  ? "Collection Hub Pickup Notes"
                  : "Pickup Location Notes"
              }
            >
              <Input 
                placeholder={
                  this.state.pickupFromType === 'health_center' 
                    ? "e.g., Ask for lab supervisor, 2nd floor" 
                    : this.state.pickupFromType === 'collection_hub'
                    ? "e.g., Use side entrance, storage room A"
                    : "Additional pickup location details"
                } 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="destination_notes"
              label={
                this.state.destinationType === 'health_center' 
                  ? "Health Center Delivery Notes" 
                  : this.state.destinationType === 'collection_hub'
                  ? "Collection Hub Delivery Notes"
                  : "Destination Notes"
              }
            >
              <Input 
                placeholder={
                  this.state.destinationType === 'health_center' 
                    ? "e.g., Deliver to lab manager, 1st floor" 
                    : this.state.destinationType === 'collection_hub'
                    ? "e.g., Drop at main counter, sign logbook"
                    : "Additional destination details"
                } 
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Route Summary */}
        {(this.state.pickupFromType || this.state.destinationType) && (
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            <Col span={24}>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f0f2f5', 
                borderRadius: '6px',
                border: '1px solid #d9d9d9'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1890ff' }}>
                  📍 Sample Transport Route
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>FROM</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {this.state.pickupFromType === 'health_center' && (
                        <span style={{ color: '#52c41a' }}>🏥 Health Center</span>
                      )}
                      {this.state.pickupFromType === 'collection_hub' && (
                        <span style={{ color: '#1890ff' }}>🏢 Collection Hub</span>
                      )}
                      {!this.state.pickupFromType && (
                        <span style={{ color: '#999' }}>Not selected</span>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '20px', color: '#1890ff' }}>→</div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>TO</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {this.state.destinationType === 'health_center' && (
                        <span style={{ color: '#52c41a' }}>🏥 Health Center</span>
                      )}
                      {this.state.destinationType === 'collection_hub' && (
                        <span style={{ color: '#1890ff' }}>🏢 Collection Hub</span>
                      )}
                      {!this.state.destinationType && (
                        <span style={{ color: '#999' }}>Not selected</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* Priority, Status and Scheduling */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="priority"
              label="Priority"
              help="Automatically set from selected sample"
            >
              <Input 
                disabled 
                placeholder="Select a sample to see priority"
                value={this.state.selectedSamplePriority || ''}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="pickup_status"
              label="Status"
            >
              <Select>
                <Option value="pending">Pending Assignment</Option>
                <Option value="assigned">Assigned to Agent</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="scheduled_pickup_date"
              label="Scheduled Pickup Date"
              help="When should this pickup be completed?"
            >
              <DatePicker 
                showTime 
                format="YYYY-MM-DD HH:mm"
                placeholder="Select pickup date and time"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Agent Assignment (Optional) */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="assigned_delivery_agent"
              label="Assign Delivery Agent/Rider (Optional)"
              help="The delivery agent will also be the rider by default"
            >
              <Select placeholder="Select delivery agent (will also be the rider)" allowClear>
                {this.state.deliveryAgents.map(agent => (
                  <Option key={agent.id} value={agent.id}>
                    {agent.FullName} - {agent.agent_code}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Notes */}
        <Form.Item
          name="notes"
          label="General Notes"
        >
          <TextArea rows={3} placeholder="Enter any additional notes, special instructions, or requirements" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={this.state.saving}>
              Create Pickup Request
            </Button>
            <Button onClick={() => this.setState({ 
              modalVisible: false, 
              pickupFromType: 'health_center',
              destinationType: 'collection_hub'
            })}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    );
  }

  renderEditForm() {
    if (!this.state.selectedPickup) return null;

    const pickup = this.state.selectedPickup;
    
    return (
      <Form
        layout="vertical"
        onFinish={this.handleEdit}
        initialValues={{
          date: pickup.date ? moment(pickup.date) : null,
          sample: pickup.sample?.id || pickup.sample,
          quantity: pickup.quantity,
          pickup_status: pickup.pickup_status,
          notes: pickup.notes,
          
          // Enhanced fields
          pickup_health_center: pickup.pickup_health_center?.id || pickup.pickup_health_center,
          pickup_collection_hub: pickup.pickup_collection_hub?.id || pickup.pickup_collection_hub,
          destination_health_center: pickup.destination_health_center?.id || pickup.destination_health_center,
          destination_collection_hub: pickup.destination_collection_hub?.id || pickup.destination_collection_hub,
          pickup_notes: pickup.pickup_notes,
          destination_notes: pickup.destination_notes,
          assigned_delivery_agent: pickup.assigned_delivery_agent?.id || pickup.assigned_delivery_agent,
          assigned_rider: pickup.assigned_rider?.id || pickup.assigned_rider,
          scheduled_pickup_date: pickup.scheduled_pickup_date ? moment(pickup.scheduled_pickup_date) : null,
          failure_reason: pickup.failure_reason,
          
          // Legacy fallbacks
          health_center: pickup.health_center?.id || pickup.health_center,
          collection_hub: pickup.collection_hub?.id || pickup.collection_hub,
          pickup_from: pickup.pickup_from,
          
          // Source tracking
          source: pickup.source,
        }}
      >
        {/* Basic Information */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="date"
              label="Pickup Date"
              rules={[{ required: true, message: 'Please select pickup date' }]}
            >
              <DatePicker 
                format="DD/MM/YYYY"
                placeholder="Select pickup date"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="sample"
              label="Sample Type"
              rules={[{ required: true, message: 'Please select sample type' }]}
            >
              <Select 
                placeholder="Select sample type"
                onChange={this.handleSampleChange}
              >
                {this.state.samples.map(sample => (
                  <Option key={sample.id} value={sample.id}>{sample.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[{ required: true, message: 'Please enter quantity' }]}
            >
              <Input type="number" placeholder="Enter quantity" min={1} />
            </Form.Item>
          </Col>
        </Row>


        {/* Location Configuration */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Pickup Location Type"
              rules={[{ required: true, message: 'Please select pickup location type' }]}
            >
              <Select 
                placeholder="Choose where to pickup samples from"
                onChange={(value) => this.setState({ pickupFromType: value })}
                value={this.state.pickupFromType}
              >
                <Option value="health_center">
                  <EnvironmentOutlined style={{ color: '#52c41a' }} /> Health Center
                  <div style={{ fontSize: '11px', color: '#666' }}>Medical facilities, clinics</div>
                </Option>
                <Option value="collection_hub">
                  <EnvironmentOutlined style={{ color: '#1890ff' }} /> Collection Hub
                  <div style={{ fontSize: '11px', color: '#666' }}>Central collection points</div>
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Destination Location Type"
              rules={[{ required: true, message: 'Please select destination location type' }]}
            >
              <Select 
                placeholder="Choose where to deliver samples to"
                onChange={(value) => this.setState({ destinationType: value })}
                value={this.state.destinationType}
              >
                <Option value="health_center">
                  <EnvironmentOutlined style={{ color: '#52c41a' }} /> Health Center
                  <div style={{ fontSize: '11px', color: '#666' }}>Medical facilities, clinics</div>
                </Option>
                <Option value="collection_hub">
                  <EnvironmentOutlined style={{ color: '#1890ff' }} /> Collection Hub
                  <div style={{ fontSize: '11px', color: '#666' }}>Central collection points</div>
                </Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Pickup and Destination Locations */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="pickup_health_center"
              label="Pickup Location"
              rules={[{ 
                required: this.state.pickupFromType === 'health_center', 
                message: 'Please select pickup health center' 
              }]}
              style={{ display: this.state.pickupFromType === 'health_center' ? 'block' : 'none' }}
            >
              <Select placeholder="Select pickup health center">
                {this.state.healthCenters.map(hc => (
                  <Option key={hc.id} value={hc.id}>{hc.name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="pickup_collection_hub"
              label="Pickup Location"
              rules={[{ 
                required: this.state.pickupFromType === 'collection_hub', 
                message: 'Please select pickup collection hub' 
              }]}
              style={{ display: this.state.pickupFromType === 'collection_hub' ? 'block' : 'none' }}
            >
              <Select placeholder="Select pickup collection hub">
                {this.state.collectionHubs.map(ch => (
                  <Option key={ch.id} value={ch.id}>{ch.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {this.state.destinationType === 'health_center' && (
              <Form.Item
                name="destination_health_center"
                label="Destination Health Center"
                rules={[{ 
                  required: true, 
                  message: 'Please select destination health center' 
                }]}
              >
                <Select 
                  placeholder="Choose which health center to deliver to"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {this.state.healthCenters.map(hc => (
                    <Option key={hc.id} value={hc.id}>{hc.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            
            {this.state.destinationType === 'collection_hub' && (
              <Form.Item
                name="destination_collection_hub"
                label="Destination Collection Hub"
                rules={[{ 
                  required: true, 
                  message: 'Please select destination collection hub' 
                }]}
              >
                <Select 
                  placeholder="Choose which collection hub to deliver to"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {this.state.collectionHubs.map(ch => (
                    <Option key={ch.id} value={ch.id}>{ch.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            
            {!this.state.destinationType && (
              <Form.Item label="Destination Location">
                <div style={{ 
                  padding: '10px', 
                  border: '1px dashed #ddd', 
                  borderRadius: '6px', 
                  color: '#999',
                  textAlign: 'center'
                }}>
                  Please select destination location type first
                </div>
              </Form.Item>
            )}
          </Col>
        </Row>

        {/* Location Notes */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="pickup_notes"
              label={
                this.state.pickupFromType === 'health_center' 
                  ? "Health Center Pickup Notes" 
                  : this.state.pickupFromType === 'collection_hub'
                  ? "Collection Hub Pickup Notes"
                  : "Pickup Location Notes"
              }
            >
              <Input 
                placeholder={
                  this.state.pickupFromType === 'health_center' 
                    ? "e.g., Ask for lab supervisor, 2nd floor" 
                    : this.state.pickupFromType === 'collection_hub'
                    ? "e.g., Use side entrance, storage room A"
                    : "Additional pickup location details"
                } 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="destination_notes"
              label={
                this.state.destinationType === 'health_center' 
                  ? "Health Center Delivery Notes" 
                  : this.state.destinationType === 'collection_hub'
                  ? "Collection Hub Delivery Notes"
                  : "Destination Notes"
              }
            >
              <Input 
                placeholder={
                  this.state.destinationType === 'health_center' 
                    ? "e.g., Deliver to lab manager, 1st floor" 
                    : this.state.destinationType === 'collection_hub'
                    ? "e.g., Drop at main counter, sign logbook"
                    : "Additional destination details"
                } 
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Priority, Status and Scheduling */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="priority"
              label="Priority"
              help="Inherited from selected sample"
            >
              <Input 
                disabled 
                value={this.state.selectedPickup?.sample?.priority || 'NORMAL'}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="pickup_status"
              label="Status"
            >
              <Select>
                <Option value="pending">Pending Assignment</Option>
                <Option value="assigned">Assigned to Agent</Option>
                <Option value="in_transit_to_pickup">En Route to Pickup</Option>
                <Option value="at_pickup_location">At Pickup Location</Option>
                <Option value="picked_up">Picked Up</Option>
                <Option value="in_transit_to_destination">En Route to Destination</Option>
                <Option value="delivered">Delivered</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="scheduled_pickup_date"
              label="Scheduled Pickup Date"
              help="When should this pickup be completed?"
            >
              <DatePicker 
                showTime 
                format="YYYY-MM-DD HH:mm"
                placeholder="Select pickup date and time"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Agent Assignment */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="assigned_delivery_agent"
              label="Assign Delivery Agent/Rider"
              help="The delivery agent will also be the rider by default"
            >
              <Select placeholder="Select delivery agent (also acts as rider)" allowClear>
                {this.state.deliveryAgents.map(agent => (
                  <Option key={agent.id} value={agent.id}>
                    {agent.FullName} - {agent.agent_code}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* Spacer for consistent layout */}
          </Col>
        </Row>

        {/* Failure Reason (only show if status is failed) */}
        {this.state.selectedPickup?.pickup_status === 'failed' && (
          <Form.Item
            name="failure_reason"
            label="Failure Reason"
          >
            <TextArea rows={2} placeholder="Please explain why the pickup/delivery failed" />
          </Form.Item>
        )}

        {/* General Notes */}
        <Form.Item
          name="notes"
          label="General Notes"
        >
          <TextArea rows={3} placeholder="Enter any additional notes, special instructions, or requirements" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={this.state.saving}>
              Update Pickup Request
            </Button>
            <Button onClick={() => this.setState({ 
              editModalVisible: false, 
              selectedPickup: null, 
              pickupFromType: 'health_center',
              destinationType: 'collection_hub'
            })}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    );
  }

  // Patient-related methods
  showPatientForm = (pickupId, patientData = null, isEdit = false) => {
    this.setState({
      patientFormVisible: true,
      currentPickupId: pickupId,
      selectedPatient: patientData,
      isEditingPatient: isEdit
    });
  };

  hidePatientForm = () => {
    this.setState({
      patientFormVisible: false,
      currentPickupId: null,
      selectedPatient: null,
      isEditingPatient: false
    });
  };

  showPatientList = (pickupId) => {
    this.setState({
      patientListVisible: true,
      currentPickupId: pickupId
    });
  };

  hidePatientList = () => {
    this.setState({
      patientListVisible: false,
      currentPickupId: null
    });
  };

  handlePatientSuccess = (patientData) => {
    message.success('Patient saved successfully!');
    this.hidePatientForm();
    // Reload data instantly to show updated information
    this.loadData();
  };

  handleAddPatient = () => {
    this.showPatientForm(this.state.currentPickupId);
  };

  handleEditPatient = (patientData) => {
    this.showPatientForm(this.state.currentPickupId, patientData, true);
  };

  handlePatientDeleted = () => {
    // Reload data instantly when a patient is deleted
    this.loadData();
  };

  render() {
    const columns = [
      {
        title: 'Pickup Number',
        dataIndex: 'pickup_number',
        key: 'pickup_number',
        ...this.getColumnSearchProps('pickup_number'),
        render: (text) => <strong>{text}</strong>,
        responsive: ['xs', 'sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        ...this.getColumnSearchProps('date'),
        responsive: ['xs', 'sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Sample',
        dataIndex: 'sample_name',
        key: 'sample_name',
        ...this.getColumnSearchProps('sample_name'),
        render: (sample_name) => sample_name || 'N/A',
        responsive: ['sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Pickup Location',
        dataIndex: 'pickup_location_display',
        key: 'pickup_location',
        render: (_, record) => (
          <Tag color="blue" icon={<EnvironmentOutlined />}>
            {record.pickup_location_display || record.pickup_from || 'N/A'}
          </Tag>
        ),
        responsive: ['md', 'lg', 'xl']
      },
      {
        title: 'Destination',
        dataIndex: 'destination_location_display', 
        key: 'destination_location',
        render: (_, record) => (
          <Tag color="green" icon={<EnvironmentOutlined />}>
            {record.destination_location_display || 'N/A'}
          </Tag>
        ),
        responsive: ['md', 'lg', 'xl']
      },
      {
        title: 'Assigned Agent/Rider',
        dataIndex: 'assigned_delivery_agent_name',
        key: 'assigned_agent',
        render: (agentName, record) => {
          if (agentName) {
            const riderName = record.assigned_rider_name;
            const isSameAgent = agentName === riderName || !riderName;
            return (
              <div>
                <Tag color="purple">{agentName}</Tag>
                {!isSameAgent && (
                  <Tag color="cyan" size="small">Rider: {riderName}</Tag>
                )}
              </div>
            );
          }
          return <Tag color="orange">Unassigned</Tag>;
        },
        responsive: ['lg', 'xl']
      },
      {
        title: 'Priority',
        dataIndex: 'sample',
        key: 'priority',
        width: 100,
        render: (sample) => {
          const priority = sample?.priority || 'normal';
          const colors = {
            low: 'blue',
            normal: 'green', 
            high: 'orange',
            urgent: 'red',
            emergency: 'volcano'
          };
          return (
            <Tag color={colors[priority] || 'default'}>
              {priority?.toUpperCase() || 'NORMAL'}
            </Tag>
          );
        },
        responsive: ['xs', 'sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: 120,
        render: (source) => {
          const sourceColors = {
            'dashboard': 'blue',
            'agent_app': 'green'
          };
          const sourceLabels = {
            'dashboard': 'Dashboard',
            'agent_app': 'Mobile App'
          };
          return (
            <Tag color={sourceColors[source] || 'default'}>
              {sourceLabels[source] || source || 'Unknown'}
            </Tag>
          );
        },
        filters: [
          { text: 'Dashboard', value: 'dashboard' },
          { text: 'Mobile App', value: 'agent_app' },
        ],
        onFilter: (value, record) => record.source === value,
        responsive: ['lg', 'xl']
      },
      {
        title: 'Created By',
        dataIndex: 'created_by',
        key: 'created_by',
        width: 150,
        render: (createdBy) => (
          <span style={{ fontWeight: 500, color: '#555' }}>
            {createdBy || 'Unknown'}
          </span>
        ),
        ...this.getColumnSearchProps('created_by'),
      },
      {
        title: 'Scheduled Pickup',
        dataIndex: 'scheduled_pickup_date',
        key: 'scheduled_pickup_date',
        render: (scheduledDate) => {
          if (scheduledDate) {
            return (
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {moment(scheduledDate).format('MMM DD, YYYY')}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>
                  {moment(scheduledDate).format('HH:mm')}
                </div>
              </div>
            );
          }
          return <span style={{ color: '#ccc' }}>Not scheduled</span>;
        }
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        render: (quantity) => <Tag color="blue">{quantity}</Tag>,
        responsive: ['sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Status',
        dataIndex: 'pickup_status',
        key: 'pickup_status',
        render: (status) => (
          <Tag color={this.getStatusColor(status)} icon={this.getStatusIcon(status)}>
            {status}
          </Tag>
        ),
        responsive: ['xs', 'sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        ellipsis: true,
        render: (notes) => notes || 'No notes',
        responsive: ['md', 'lg', 'xl']
      },
      {
        title: 'Patients',
        key: 'patients',
        width: 120,
        render: (_, record) => (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<UserOutlined />}
              onClick={() => this.showPatientList(record.id)}
            >
              View
            </Button>
            <Button
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => this.showPatientForm(record.id)}
            >
              Add
            </Button>
          </Space>
        ),
        responsive: ['xs', 'sm', 'md', 'lg', 'xl']
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 150,
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        render: (_, record) => (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => this.showEditModal(record)}
            >
              View
            </Button>
            {this.state.userrights?.create_update_pending_umoja_pickups && (
              <>
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => this.showEditModal(record)}
                >
                  Edit
                </Button>
                <Button
                  type="default"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => this.handleDelete(record.id)}
                >
                  Delete
                </Button>
              </>
            )}
          </Space>
        ),
        responsive: ['lg', 'xl']
      },
    ];

    if (this.state.datarequested) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div>
        <Collapse defaultActiveKey={['1']}>
          <Panel header="PENDING UMOJA PICKUPS" key="1">
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={24} md={12}>
                <h3>Pending Umoja Pickups Management</h3>
                <p>Manage and track pending medical sample pickups from health centers, collection hubs, or other locations</p>
              </Col>
              <Col xs={24} sm={24} md={12} style={{ textAlign: 'right' }}>
                {/* Show button if user has rights */}
                {this.userrights?.create_update_pending_umoja_pickups && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={this.showCreateModal}
                    size="large"
                  >
                    Create New Pickup
                  </Button>
                )}
                
                {/* Temporary button for testing - remove this later */}
                {!this.userrights?.create_update_pending_umoja_pickups && (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={this.showCreateModal}
                    size="large"
                    style={{ marginLeft: '10px' }}
                  >
                    Create New Pickup
                  </Button>
                )}
                
                {/* Export Buttons */}
                <Space style={{ marginLeft: '10px' }}>
                  <Tooltip title="Export to Excel">
                    <Button
                      type="default"
                      icon={<FileExcelOutlined />}
                      onClick={this.exportToExcel}
                      size="large"
                    >
                      Excel
                    </Button>
                  </Tooltip>
                  
                  <ReactToPrint
                    trigger={() => (
                      <Tooltip title="Print / Save as PDF">
                        <Button
                          type="default"
                          icon={<PrinterOutlined />}
                          size="large"
                        >
                          Print/PDF
                        </Button>
                      </Tooltip>
                    )}
                    content={() => this.printRef.current}
                    documentTitle="Umoja Sample Pickups Report"
                  />
                </Space>
              </Col>
            </Row>

            {/* Date Range Filter */}
            <Row style={{ marginBottom: '16px', marginTop: '16px' }}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '500', color: '#595959' }}>Filter by Date Range:</span>
                  <RangePicker
                    value={this.state.dateRange}
                    onChange={this.handleDateRangeChange}
                    format="DD/MM/YYYY"
                    placeholder={['Start Date', 'End Date']}
                    allowClear={false}
                    size="large"
                    style={{ minWidth: '250px', width: '300px' }}
                  />
                  
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />}
                    onClick={this.handleSearchByDateRange}
                    size="large"
                    loading={this.state.datarequested}
                  >
                    Search
                  </Button>
                  
                  {/* Quick Preset Buttons */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button 
                      size="small" 
                      onClick={() => this.setDateRangePreset(7)}
                      type={this.state.dateRange && this.state.dateRange[0].isSame(moment().subtract(7, 'days'), 'day') ? 'primary' : 'default'}
                    >
                      Last Week
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => this.setDateRangePreset(14)}
                      type={this.state.dateRange && this.state.dateRange[0].isSame(moment().subtract(14, 'days'), 'day') ? 'primary' : 'default'}
                    >
                      2 Weeks
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => this.setDateRangePreset(30)}
                      type={this.state.dateRange && this.state.dateRange[0].isSame(moment().subtract(30, 'days'), 'day') ? 'primary' : 'default'}
                    >
                      Last Month
                    </Button>
                  </div>
                  
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    Showing {this.state.pendingPickups.length} pickup(s) 
                    {this.state.dateRange && this.state.dateRange.length === 2 && (
                      ` from ${this.state.dateRange[0].format('DD/MM/YYYY')} to ${this.state.dateRange[1].format('DD/MM/YYYY')}`
                    )}
                  </span>
                </div>
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={this.state.pendingPickups}
              rowKey="id"
              pagination={{
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                responsive: true,
                size: 'small'
              }}
              scroll={{ x: 1200 }}
              bordered
              size="small"
              responsive
            />
          </Panel>
        </Collapse>

        {/* Create Modal */}
        <Modal
          title="Create New Pending Pickup"
          visible={this.state.modalVisible}
          onCancel={() => this.setState({ modalVisible: false, pickupFromType: 'health_center' })}
          footer={null}
          width="90%"
          style={{ maxWidth: 700 }}
          bodyStyle={{ padding: '16px' }}
        >
          {this.renderCreateForm()}
        </Modal>

        {/* Edit Modal */}
        <Modal
          title="Edit Pending Pickup"
          visible={this.state.editModalVisible}
          onCancel={() => this.setState({ editModalVisible: false, selectedPickup: null, pickupFromType: 'health_center' })}
          footer={null}
          width="90%"
          style={{ maxWidth: 700 }}
          bodyStyle={{ padding: '16px' }}
        >
          {this.renderEditForm()}
        </Modal>

        {/* Patient Form Modal */}
        <PatientForm
          visible={this.state.patientFormVisible}
          onCancel={this.hidePatientForm}
          onSuccess={this.handlePatientSuccess}
          pendingPickupId={this.state.currentPickupId}
          patientData={this.state.selectedPatient}
          isEdit={this.state.isEditingPatient}
        />

        {/* Patient List Modal */}
        <PatientList
          visible={this.state.patientListVisible}
          onClose={this.hidePatientList}
          pendingPickupId={this.state.currentPickupId}
          onAddPatient={this.handleAddPatient}
          onEditPatient={this.handleEditPatient}
          onPatientDeleted={this.handlePatientDeleted}
        />

        {/* Hidden printable content */}
        <div style={{ display: 'none' }}>
          <div ref={this.printRef}>
            {this.renderPrintableContent()}
          </div>
        </div>
      </div>
    );
  }
}

export default PendingUmojaPickupsList;
